import { getAxiosInstance, loadAppConfig  } from '@chr/online-shared-microsite-core';
import { AxiosInstance } from 'axios';
import {
  Application,
  ReadApplicationsResponse,
  IdentityPasswordPolicy,
  ReadPasswordPoliciesResponse,
  UserInvitation,
  ReadUserByInvitationIdResponse,
  UpdateInvitationById,
  UpdateInvitationResponse,
  ResponseStatus,
  ReadClassicUsersMapResponse,
  MergeOnlineOneProfileToOnlineTwoUserResponse,
  RemoveOnlineOneProfileFromOnlineTwoUserResponse,
  ProfileConfirmation,
  ReadProfileConfirmationResponse,
  SendProfileConfirmationResponse,
  SubmitProfileConfirmationResponse,
  ConfirmProfileConfirmationResponse,
} from 'appDtos';

export const readApplications = async (): Promise<Application[]> => {
  const axiosInstance: AxiosInstance = await getAxiosInstance();
  const { gatewayApiUri } = await loadAppConfig();

  const uri = `${gatewayApiUri}/Applications`;

  const response = await axiosInstance.get<ReadApplicationsResponse>(
    encodeURI(uri)
  );

  return response.data.applications ? response.data.applications : [];
};

export const readUserInvitation = async (invitationId: number, token: string): Promise<UserInvitation | ResponseStatus> => {
  const axiosInstance: AxiosInstance = await getAxiosInstance();
  const { gatewayApiUri } = await loadAppConfig();

  const uri = `${gatewayApiUri}/Invitations/${invitationId}?token=${token}`;

  try {
    const response = await axiosInstance.get<ReadUserByInvitationIdResponse>(encodeURI(uri));
    return response.data.userInvitation;
  }
  catch(error: any) {
    return error.response.data.responseStatus;
  }
};

export const readPasswordPolicy = async (): Promise<IdentityPasswordPolicy[]> => {
  const axiosInstance: AxiosInstance = await getAxiosInstance();
  const { gatewayApiUri } = await loadAppConfig();

  const uri = `${gatewayApiUri}/Policies/Password`;

  const response = await axiosInstance.get<ReadPasswordPoliciesResponse>(
    encodeURI(uri)
  );

  return response.data.passwordPolicy;
};

export const setPassword = async (invitationId: number, token: string, password: string): Promise<UpdateInvitationById> => {
  const axiosInstance: AxiosInstance = await getAxiosInstance();
  const { gatewayApiUri } = await loadAppConfig();

	const invitationData = {
    invitationId : invitationId,
	  token : token,
    password: password,
  }

  const response = await axiosInstance.put<UpdateInvitationResponse>(`${gatewayApiUri}/Invitations/${invitationId}`, invitationData);
  return response.request;
};

export const readClassicUsersMap = async (userId: number): Promise<ReadClassicUsersMapResponse> => {
  const axiosInstance: AxiosInstance = await getAxiosInstance();
  const { gatewayApiUri } = await loadAppConfig();

  const uri = `${gatewayApiUri}/Users/${userId}/Map`;

  const response = await axiosInstance.get<ReadClassicUsersMapResponse>(
    encodeURI(uri)
  );

  return response.data;
};

export const mergeOnlineOneProfileToCustomerPlatformUser = async(userId: number, email: string, userName: string, password: string): Promise<string> => {
  const axiosInstance: AxiosInstance = await getAxiosInstance();
  const { gatewayApiUri } = await loadAppConfig();

  const credentials = {
    userId : userId,
    email: email,
    onlineOneUserName : userName,
    onlineOneUserPassword: password,
  };

  const response = await axiosInstance.post<MergeOnlineOneProfileToOnlineTwoUserResponse>(`${gatewayApiUri}/Users/${userId}/MergeOnlineOneProfile`, credentials);
  return response.data.errorMessage;
};

export const removeOnlineOneProfileFromCustomerPlatformUser = async(userId: number, email: string, onlineOneUserId: number): Promise<void> => {
  const axiosInstance: AxiosInstance = await getAxiosInstance();
  const { gatewayApiUri } = await loadAppConfig();

  const encodedEmail = encodeURIComponent(email);

  await axiosInstance.delete<RemoveOnlineOneProfileFromOnlineTwoUserResponse>(`${gatewayApiUri}/Users/${userId}/RemoveOnlineOneProfile/${onlineOneUserId}?Email=${encodedEmail}`);
};

export const readProfileConfirmation = async (guid:string): Promise<ProfileConfirmation> => {
  const axiosInstance: AxiosInstance = await getAxiosInstance();
  const { gatewayApiUri } = await loadAppConfig();

  const uri = `${gatewayApiUri}/ProfileConfirmation/${guid}`;

  const response = await axiosInstance.get<ReadProfileConfirmationResponse>(
    encodeURI(uri)
  );

  return response.data.profileConfirmation;
};

export const sendProfileConfirmation = async (guid:string): Promise<void> => {
  const axiosInstance: AxiosInstance = await getAxiosInstance();
  const { gatewayApiUri } = await loadAppConfig();

  const uri = `${gatewayApiUri}/ProfileConfirmation/${guid}/SendConfirmation`;

  await axiosInstance.put<SendProfileConfirmationResponse>(encodeURI(uri));
};

export const submitProfileConfirmation = async (profile:ProfileConfirmation) : Promise<void> => {
  const axiosInstance: AxiosInstance = await getAxiosInstance();
  const { gatewayApiUri } = await loadAppConfig();

  const uri = `${gatewayApiUri}/ProfileConfirmation/${profile.profileConfirmationId}/Submission`;
  const requestData = {
    guid: profile.profileConfirmationId,
    firstName: profile.firstName,
    lastName: profile.lastName,
    companyName: profile.companyName,
    email: profile.email
  };

  await axiosInstance.put<SubmitProfileConfirmationResponse>(encodeURI(uri), requestData);
};

export const confirmProfileConfirmation = async (guid:string, token: string): Promise<ProfileConfirmation> => {
  const axiosInstance: AxiosInstance = await getAxiosInstance();
  const { gatewayApiUri } = await loadAppConfig();

  const uri = `${gatewayApiUri}/ProfileConfirmation/${guid}/Confirmation`;

  const response = await axiosInstance.put<ConfirmProfileConfirmationResponse>(
    encodeURI(uri),
    {token: token}
  );

  return response.data.profileConfirmation;
};
